/* eslint-disable react/no-array-index-key */
import {
  Children,
  useState,
  forwardRef,
  useCallback,
  useEffect,
  useRef
} from 'react';
import PropTypes from 'prop-types';
import Grow from '@base/Grow/Grow';
import Link from '@base/Link/Link';
import ChevronLeft from '@icon/ChevronLeft';
import ChevronRight from '@icon/ChevronRight';
import useWindowSize from '@hooks/useWindowSize';
import pushRoute from '@utils/pushRoute';
import {
  StyH4,
  BoldP1,
  ItemWrap,
  CarouselWrapper,
  CardContainer,
  ControlButton,
  CaraouselInfo,
  NavButtonsContainer,
  NavButton,
  ScrollDotsContainer,
  ScrollDot,
  SeeAll,
  InfoSectionsDiv,
  Description
} from './Carousel.styles';

const Carousel = forwardRef(
  (
    {
      children,
      label,
      seeAllLink,
      href,
      showControls,
      className,
      numberOfItems,
      minItems,
      gridColumn,
      itemGridColumn,
      gridColumnGap,
      showNavButtons,
      showLabel,
      viewRef,
      showInfo,
      showBottomGap,
      $css,
      autoScroll,
      autoScrollDelay,
      handleClickSeeAll,
      labelChild,
      isUnIconArrow,
      gridTemplateColumns,
      description,
      isPrime,
      mobileItemMinWidth,
      showScrollDots
    },
    ref
  ) => {
    const cardContainerRef = useRef(null);
    const { isMobile } = useWindowSize();
    const items = Children.toArray(children);
    const [pos, setPos] = useState(0);
    const [activeScrollDot, setActiveScrollDot] = useState(0);
    const showNavigationButtons = showNavButtons && items.length > 1;

    const finalItems = items.map((item, index) => {
      const gridColumnForItem =
        itemGridColumn || item.props.gridColumn || 'span 6';
      const endPoint = pos + (numberOfItems - 1);
      const startPoint = pos;
      const opacity = index >= startPoint && index <= endPoint ? 1 : 0;
      return (
        <ItemWrap gridColumn={gridColumnForItem} opacity={opacity} key={index}>
          {item}
        </ItemWrap>
      );
    });

    const handleNext = useCallback(() => {
      if (items.length - numberOfItems - 1 >= pos - minItems) setPos(pos + 1);
    }, [items.length, numberOfItems, minItems, pos]);

    const handlePrev = () => {
      if (pos > 0) setPos(pos - 1);
    };

    useEffect(() => {
      let timer = null;
      if (autoScroll) {
        timer = setInterval(() => {
          if (isPrime) {
            setPos((prevPos) => {
              const maxPos = isMobile
                ? items.length - 1
                : items.length - numberOfItems + minItems;
              return prevPos < maxPos ? prevPos + 1 : 0;
            });
          } else if (items.length - numberOfItems - 1 >= pos) {
            handleNext();
          } else {
            setPos(0);
          }
        }, autoScrollDelay);
      }
      return () => {
        clearInterval(timer);
      };
    }, [
      autoScroll,
      autoScrollDelay,
      handleNext,
      isMobile,
      items,
      numberOfItems,
      isPrime,
      minItems,
      pos
    ]);

    useEffect(() => {
      // Reset position to 0 on resizing between screen sizes to avoid the disappearing carousel problem.
      setPos(0);
      if (cardContainerRef.current) cardContainerRef.current.scrollLeft = 0;
    }, [isMobile]);

    useEffect(() => {
      const container = cardContainerRef.current;
      if (!showScrollDots || !container) return undefined;

      const handleScroll = () => {
        const { scrollLeft, scrollWidth, clientWidth } = container;
        const maxScroll = scrollWidth - clientWidth;
        if (maxScroll <= 0) {
          setActiveScrollDot(0);
          return;
        }
        const dotIndex = Math.round(
          (scrollLeft / maxScroll) * (items.length - 1)
        );

        setActiveScrollDot(dotIndex);
      };

      container.addEventListener('scroll', handleScroll, { passive: true });
      return () => container.removeEventListener('scroll', handleScroll);
    }, [showScrollDots, items.length]);

    const controlButtons = () => {
      if (!showControls || items.length <= numberOfItems - minItems)
        return null;
      const prevAvailable = pos > 0;
      const nextAvailable = pos - minItems < items.length - numberOfItems;
      const iconColor = (() => {
        if (isPrime) return 'var(--color-i-violet-15)';

        if (isUnIconArrow) {
          return 'var(--color-i-icon-light-tertiary)';
        }

        return 'var(--color-text-primary)';
      })();
      return (
        <>
          {prevAvailable ? (
            <ControlButton
              onClick={handlePrev}
              direction="prev"
              bgColor={
                isUnIconArrow
                  ? 'var(--color-i-icon-dark)'
                  : 'var(--color-base-1)'
              }
              hovDisabled
              $isPrime={isPrime}
              $topAdjustment={showNavigationButtons ? '18px' : '0px'}
            >
              <ChevronLeft size="24px" aria-hidden="true" color={iconColor} />
            </ControlButton>
          ) : null}
          {nextAvailable ? (
            <ControlButton
              onClick={handleNext}
              direction="next"
              $isPrime={isPrime}
              bgColor={
                isUnIconArrow
                  ? 'var(--color-i-icon-dark)'
                  : 'var(--color-base-1)'
              }
              hovDisabled
              $isUnIconArrow={isUnIconArrow}
              $topAdjustment={showNavigationButtons ? '18px' : '0px'}
            >
              <ChevronRight size="24px" aria-hidden="true" color={iconColor} />
            </ControlButton>
          ) : null}
        </>
      );
    };

    const renderNavButtons = () => {
      const handlenavButtonClick = (position) => () => {
        setPos(position);
      };
      if (showNavigationButtons) {
        const navbuttons = items.map((_, index) => (
          <NavButton
            onClick={handlenavButtonClick(index)}
            key={index}
            $isSelected={index === pos}
          />
        ));
        return (
          <NavButtonsContainer $gridColumn={gridColumn}>
            {navbuttons}
          </NavButtonsContainer>
        );
      }
      return null;
    };

    const seeAllAnalytics = () => {
      if (handleClickSeeAll) {
        handleClickSeeAll();
      }
    };

    const onClickSeeAll = () => {
      pushRoute(href, seeAllLink);
    };

    return (
      <>
        {showInfo && (
          <CaraouselInfo ref={ref} className={className}>
            {showLabel && items.length > 0 && labelChild ? labelChild : null}
            {showLabel && items.length > 0 && !labelChild && label ? (
              <InfoSectionsDiv>
                <StyH4 as="h2" $isPrime={isPrime}>
                  {label}
                </StyH4>
                {description && (
                  <Description variant="p2">{description}</Description>
                )}
              </InfoSectionsDiv>
            ) : null}
            <Grow />
            {seeAllLink && (
              <Link as={seeAllLink} href={href} onClick={seeAllAnalytics}>
                <BoldP1
                  color={
                    isPrime
                      ? 'var(--color-i-violet-15)'
                      : 'var(--color-i-green)'
                  }
                >
                  SEE ALL
                </BoldP1>
              </Link>
            )}
          </CaraouselInfo>
        )}
        <CarouselWrapper
          gridColumn={gridColumn}
          ref={viewRef}
          $css={$css}
          $hasSeeAll={seeAllLink && isMobile}
        >
          <CardContainer
            $mobileItemMinWidth={mobileItemMinWidth}
            $gridColumnGap={gridColumnGap}
            $showBottomGap={showBottomGap}
            $gridTemplateColumns={gridTemplateColumns}
            pos={pos}
            $numberOfItemsToShow={numberOfItems}
            $nItems={finalItems.length}
            ref={cardContainerRef}
          >
            {finalItems}
          </CardContainer>
          {controlButtons()}
        </CarouselWrapper>
        {renderNavButtons()}
        {showScrollDots &&
          items.length > 1 &&
          (() => {
            const totalDots = isMobile
              ? items.length
              : items.length - numberOfItems + minItems + 1;
            const activeDot =
              isMobile && !(isPrime && autoScroll) ? activeScrollDot : pos;
            return (
              <ScrollDotsContainer $gridColumn={gridColumn}>
                {Array.from({ length: totalDots }, (_, index) => (
                  <ScrollDot key={index} $isActive={index === activeDot} />
                ))}
              </ScrollDotsContainer>
            );
          })()}
        {!seeAllLink || isPrime ? null : (
          <SeeAll label="See All" onClick={onClickSeeAll} />
        )}
      </>
    );
  }
);

Carousel.displayName = 'Carousel';

Carousel.propTypes = {
  showInfo: PropTypes.bool,
  showBottomGap: PropTypes.bool,
  children: PropTypes.node.isRequired,
  label: PropTypes.string,
  seeAllLink: PropTypes.string,
  href: PropTypes.string,
  showControls: PropTypes.bool,
  numberOfItems: PropTypes.number,
  minItems: PropTypes.number,
  gridColumn: PropTypes.string,
  itemGridColumn: PropTypes.string,
  gridColumnGap: PropTypes.string,
  className: PropTypes.string,
  showNavButtons: PropTypes.bool,
  gridTemplateColumns: PropTypes.number,
  showLabel: PropTypes.bool,
  viewRef: PropTypes.shape({ current: PropTypes.oneOfType([PropTypes.any]) }),
  $css: PropTypes.string,
  autoScroll: PropTypes.bool,
  autoScrollDelay: PropTypes.number,
  handleClickSeeAll: PropTypes.func,
  isUnIconArrow: PropTypes.bool,
  description: PropTypes.string,
  isPrime: PropTypes.bool,
  mobileItemMinWidth: PropTypes.string,
  showScrollDots: PropTypes.bool
};

Carousel.defaultProps = {
  showInfo: true,
  gridTemplateColumns: 600,
  showBottomGap: true,
  label: null,
  seeAllLink: null,
  href: null,
  showControls: true,
  numberOfItems: 3,
  minItems: 0,
  gridColumn: 'span 18',
  itemGridColumn: null,
  gridColumnGap: null,
  className: '',
  showNavButtons: false,
  showLabel: true,
  viewRef: null,
  $css: '',
  autoScroll: false,
  autoScrollDelay: 5000,
  handleClickSeeAll: () => {},
  isUnIconArrow: false,
  description: '',
  isPrime: false,
  mobileItemMinWidth: '250px',
  showScrollDots: false
};
export default Carousel;
